import React, { useEffect } from 'react';
import { StyleSheet, Animated, Modal, Pressable } from 'react-native';

import { useTheme } from '@/components/theme-provider';
import { Button } from '@/components/ui';
import { Text } from '@/components/ui';
import type { NotificationItem } from '@/utils/notification-manager';
import { claimTouchResponder } from '@/utils/responder';

interface ModalNotificationProps {
	notification: NotificationItem;
	onDismiss: (id: string) => void;
}

export const ModalNotification: React.FC<ModalNotificationProps> = ({
	notification,
	onDismiss,
}) => {
	const theme = useTheme();
	const scale = React.useRef(new Animated.Value(0)).current;
	const opacity = React.useRef(new Animated.Value(0)).current;

	useEffect(() => {
		// Animate in
		Animated.parallel([
			Animated.timing(scale, {
				toValue: 1,
				duration: 300,
				useNativeDriver: true,
			}),
			Animated.timing(opacity, {
				toValue: 1,
				duration: 300,
				useNativeDriver: true,
			}),
		]).start();
	}, [scale, opacity]);

	const handleDismiss = () => {
		Animated.parallel([
			Animated.timing(scale, {
				toValue: 0,
				duration: 200,
				useNativeDriver: true,
			}),
			Animated.timing(opacity, {
				toValue: 0,
				duration: 200,
				useNativeDriver: true,
			}),
		]).start(() => {
			onDismiss(notification.id);
		});
	};

	const getVariantStyle = () => {
		switch (notification.variant) {
			case 'success':
				return { borderLeftColor: theme.colors.primary, borderLeftWidth: 4 };
			case 'error':
				return { borderLeftColor: theme.colors.error, borderLeftWidth: 4 };
			case 'warning':
				return { borderLeftColor: theme.colors.tertiary, borderLeftWidth: 4 };
			case 'info':
			default:
				return { borderLeftColor: theme.colors.primary, borderLeftWidth: 4 };
		}
	};

	return (
		<Modal
			visible={true}
			transparent
			animationType="none"
			onRequestClose={handleDismiss}
		>
			<Pressable style={styles.backdrop} onPress={handleDismiss}>
				<Animated.View
					style={[
						styles.container,
						{
							backgroundColor: theme.colors.background,
							opacity,
							transform: [{ scale }],
						},
						getVariantStyle(),
					]}
					{...claimTouchResponder}
				>
					{notification.title && (
						<Text variant="headlineSmall" style={styles.title}>
							{notification.title}
						</Text>
					)}

					{notification.message && (
						<Text variant="bodyMedium" style={styles.message}>
							{notification.message}
						</Text>
					)}

					{notification.actions && notification.actions.length > 0 && (
						<Animated.View style={styles.actions}>
							{notification.actions.map((action, index) => (
								<Button
									key={index}
									mode={action.style === 'destructive' ? 'contained' : 'text'}
									buttonColor={action.style === 'destructive' ? theme.colors.error : undefined}
									textColor={action.style === 'destructive' ? theme.colors.onError : undefined}
									onPress={() => {
										action.onPress();
										handleDismiss();
									}}
									style={styles.actionButton}
								>
									{action.label}
								</Button>
							))}
						</Animated.View>
					)}
				</Animated.View>
			</Pressable>
		</Modal>
	);
};

const styles = StyleSheet.create({
	backdrop: {
		flex: 1,
		backgroundColor: 'rgba(0, 0, 0, 0.5)',
		justifyContent: 'center',
		alignItems: 'center',
		padding: 20,
	},
	container: {
		backgroundColor: 'white',
		borderRadius: 12,
		padding: 24,
		maxWidth: 500,
		minWidth: 300,
		elevation: 8,
		shadowColor: '#000',
		shadowOffset: {
			width: 0,
			height: 4,
		},
		shadowOpacity: 0.25,
		shadowRadius: 8,
	},
	title: {
		marginBottom: 8,
		fontWeight: '600',
	},
	message: {
		marginBottom: 16,
		lineHeight: 24,
	},
	actions: {
		flexDirection: 'row',
		justifyContent: 'flex-end',
		gap: 8,
	},
	actionButton: {
		minWidth: 80,
	},
});
